import {
  MouseEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import PercentBar from '@atom/components/common/percentBar/PercentBar';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Checkbox, Icon, ListTable } from '@atom/mui';
import colors from '@atom/styles/colors';
import { BudgetUnit } from '@atom/types/budget';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BudgetDetailComparison from '../BudgetDetailComparison';
import BudgetDetailContext from '../BudgetDetailContext';
import {
  BUDGET_COL_WIDTH,
  getComparisonIcon,
  getStatusLabel,
  getTypeName,
  isStatusLocked,
  SELECT_COL_WIDTH,
} from '../budgetDetailUtils';

import classes from '../budgetDetail.module.css';

const { TableRow, TableCell } = ListTable;

interface Props {
  budgetUnit: BudgetUnit;
  displayTypeColumn?: boolean;
}

const BudgetDetailChildUnitRow = ({ budgetUnit, displayTypeColumn }: Props) => {
  const [, { navigate }] = useNavigation();
  const {
    budget,
    categoryIds,
    budgetTypes,
    budgetItemTemplateNames,
    budgetSummaries,
    comparisonBudgets,
    openComparisonRowId,
    setOpenComparisonRowId,
    fetchSummaries,
    loadingSummaries,
    showTracking,
    selectedUnits,
    setSelectedUnits,
    showApprovalFlow,
  } = useContext(BudgetDetailContext);
  const statusLocked = isStatusLocked(budget?.id);

  const [hovering, setHovering] = useState<boolean>(false);

  const open: boolean = useMemo(
    () => openComparisonRowId === budgetUnit.id,
    [openComparisonRowId],
  );

  const StatusLabel = useMemo(() => {
    return getStatusLabel(budgetUnit?.status);
  }, [budgetUnit?.status]);

  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && !isNilOrEmpty(comparisonBudgets)) {
      fetchSummaries({
        variables: {
          input: {
            budgetUnitId: budgetUnit.id,
            budgetIds: R.pluck('id', comparisonBudgets),
            categoryIds,
            itemNames: budgetItemTemplateNames,
            budgetTypes,
          },
        },
      });
    }
  }, [open, comparisonBudgets]);

  const handleNavigateToUnit = () => {
    navigate(`/budget/${budget.id}/${budgetUnit.id}`);
  };

  const handleCompareClick = (event: MouseEvent) => {
    event.stopPropagation();
    setOpenComparisonRowId(open ? null : budgetUnit.id);
  };

  return (
    <>
      <TableRow
        onClick={() => handleNavigateToUnit()}
        style={{
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        {showApprovalFlow && !statusLocked && (
          <TableCell
            width={SELECT_COL_WIDTH}
            onClick={event => event.stopPropagation()}
          >
            <Checkbox
              checked={selectedUnits.has(budgetUnit.id)}
              onClick={() =>
                setSelectedUnits(toggleFromSet(selectedUnits, budgetUnit.id))
              }
            />
          </TableCell>
        )}
        <TableCell>
          <span className={classes.budgetTableCell}>
            <Icon
              color={
                budgetUnit.hasBudgetItems
                  ? colors.utility.activeBlue
                  : colors.neutral.gray
              }
            >
              folder
            </Icon>
            {budgetUnit.name}
          </span>
        </TableCell>
        {displayTypeColumn && (
          <TableCell>
            <span className={classes.budgetTableCell}>
              {getTypeName(budgetUnit)}
            </span>
          </TableCell>
        )}
        {showApprovalFlow && (
          <TableCell>
            <span className={classes.budgetTableCell}>{StatusLabel}</span>
          </TableCell>
        )}
        <TableCell align="right">
          <span className={classes.budgetTableCell}>
            {numberToLocaleString(budgetUnit?.totalBudget)}
          </span>
        </TableCell>
        {showTracking && (
          <>
            <TableCell align="right">
              <span className={classes.budgetTableCell}>
                {numberToLocaleString(budgetUnit?.actualBudget)}
              </span>
            </TableCell>
            <TableCell align="right">
              <span className={classes.budgetTableCell}>
                {numberToLocaleString(budgetUnit?.fixedCosts)}
              </span>
            </TableCell>
            <TableCell width={BUDGET_COL_WIDTH} align="right">
              <PercentBar
                value={budgetUnit?.remaining}
                limit={budgetUnit?.totalBudget}
              />
            </TableCell>
          </>
        )}
        <TableCell>
          <span
            ref={anchorRef}
            onClick={handleCompareClick}
            className={clsx(classes.budgetTableCell, classes.clickable)}
          >
            {getComparisonIcon(hovering, open)}
          </span>
        </TableCell>
      </TableRow>
      {open && (
        <BudgetDetailComparison
          handleCloseModal={() => setOpenComparisonRowId(null)}
          loadingSummaries={loadingSummaries}
          budgetSummaries={budgetSummaries}
          anchorEl={anchorRef?.current}
          onClickAway={() => setOpenComparisonRowId(null)}
        />
      )}
    </>
  );
};

export default BudgetDetailChildUnitRow;
